import {
  Box,
  Button,
  Flex,
  Heading,
  Dialog,
  Text,
  DialogRootProps,
  DialogContentProps,
  DialogHeaderProps,
  DialogBodyProps,
  DialogFooterProps,
} from '@chakra-ui/react';
import { ReactNode } from 'react';
import { useIntercom } from 'react-use-intercom';
import RobotIcon from 'src/assets/icons/RobotIcon';

interface DialogProps {
  open: boolean;
  onClose: () => void;
  heading?: string;
  Body: ReactNode;
  Footer?: ReactNode;
  width?: string;
  subheading?: string;
  onTryAgain?: () => void;
  errorMessage?: string;
  tryAgainButtonText?: string;
}

export default function ErrorDialog({
  open,
  onClose,
  heading,
  subheading,
  Body: BodyComp,
  Footer: FooterComp,
  width,
  onTryAgain,
  errorMessage,
  tryAgainButtonText,
}: DialogProps) {
  const { showNewMessage: sendIntercomMessage } = useIntercom();
  const intercomMessageTemplate = errorMessage
    ? `Help! I got an error message that says "${errorMessage}"`
    : '';
  return (
    <Container open={open} onOpenChange={e => !e.open && onClose()} closeOnInteractOutside={false}>
      <Content width={width || '360px'}>
        <Header heading={heading || 'Something went wrong...'} subheading={subheading} />
        <Body>{BodyComp}</Body>
        <Footer>
          {FooterComp ?? (
            <Flex>
              <Button h="42px" mr="8px" onClick={onTryAgain || onClose}>
                {tryAgainButtonText || 'Try again'}
              </Button>
              <Button
                visual="tertiary"
                h="42px"
                onClick={() => {
                  sendIntercomMessage(intercomMessageTemplate);
                }}
              >
                Contact Support
              </Button>
            </Flex>
          )}
        </Footer>
      </Content>
    </Container>
  );
}

function Container(props: DialogRootProps) {
  return (
    <Dialog.Root placement="center" scrollBehavior="inside" trapFocus={false} {...props}>
      <Dialog.Backdrop />
      {props.children}
    </Dialog.Root>
  );
}

function Content(props: DialogContentProps) {
  return <Dialog.Content w={props.width || '600px'} maxH="95vh" maxW="95vw" {...props} />;
}

function Header({
  heading,
  subheading,
  ...props
}: DialogHeaderProps & { heading: string; subheading?: string }) {
  return (
    <Box mt="30px" mx="30px" {...props}>
      <RobotIcon />
      <Dialog.CloseTrigger h="42px" w="42px" borderRadius="8px" right="25px" top="27px" />
      <Heading color="podcastShade600" size="h5" my="8px">
        {heading}
      </Heading>
      {subheading && (
        <Text textAlign="start" mt="8px">
          {subheading}
        </Text>
      )}
    </Box>
  );
}

function Body(props: DialogBodyProps) {
  return <Box px="30px" {...props} />;
}

function Footer(props: DialogFooterProps) {
  return <Box p="18px 8px 30px 30px" {...props} />;
}
